/**
 * Meta Pixel + GTM capability layer.
 *
 * Does NOT install the Pixel. Marketing adds the <script> via GTM or directly.
 * All fbq() calls are defensive: window.fbq?.(...) — silent no-op if Pixel absent.
 * All events also push to window.dataLayer so GTM/GA picks them up without rework.
 *
 * LP-route gate (React app only):
 *   initTracking() activates listeners only when the current path includes "/lp-".
 *   Non-LP routes (the assessment tool) get zero tracking. If the user navigates
 *   from an LP route to a non-LP route mid-session, disableTracking() is called
 *   automatically. The static AI-readiness LP runs its own standalone tracking.js
 *   and is unaffected by this module.
 *
 * OTP cutoff:
 *   After OTP verification succeeds, authService.js calls window.__trackOTPSuccess?.()
 *   which fires the final conversion events and permanently kills all listeners for
 *   the rest of the session (killed flag persists even if user navigates back to LP).
 *
 * Usage:
 *   import { initTracking } from './utils/tracking';
 *   // In App.js top-level useEffect:
 *   initTracking();
 */

// ─── PII detection ────────────────────────────────────────────────────────────

const PII_PATTERN = /email|phone|mobile|name/i;

function isPII(fieldName, fieldType, fieldId) {
  return (
    PII_PATTERN.test(fieldName || '') ||
    PII_PATTERN.test(fieldType || '') ||
    PII_PATTERN.test(fieldId || '')
  );
}

// ─── SHA-256 helper ───────────────────────────────────────────────────────────

/**
 * Returns lowercase hex SHA-256 of the trimmed, lowercased input value.
 * Used for email/phone/name before sending as Meta advanced matching params.
 * Returns empty string if SubtleCrypto is unavailable or value is empty.
 */
export async function hashPII(value) {
  if (!value || typeof value !== 'string') return '';
  const normalized = value.trim().toLowerCase();
  if (!normalized) return '';
  try {
    const encoded = new TextEncoder().encode(normalized);
    const buf = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return '';
  }
}

// ─── Kill flag + state ────────────────────────────────────────────────────────

// Permanently set to true after OTP success. Never reset — survives route changes
// in the same session. Any event fire after this is a no-op.
let killed = false;

// Set to true once initTracking() attaches listeners.
let isInitialized = false;

// Track sections that have already been reported (reset per-page-load, not per-route).
const seenSections = new Set();

// ─── Core emit functions ──────────────────────────────────────────────────────

/**
 * Fire a Meta custom event + push to dataLayer.
 * No-ops if tracking has been killed.
 */
export function trackEvent(name, properties) {
  if (killed) return;
  window.fbq?.('trackCustom', name, properties);
  if (window.dataLayer) {
    window.dataLayer.push({ event: name, ...properties });
  }
}

/**
 * Fire a Meta standard event (PageView, Lead, CompleteRegistration, etc.) + dataLayer.
 * No-ops if tracking has been killed.
 */
export function trackStandardEvent(name, properties) {
  if (killed) return;
  window.fbq?.('track', name, properties);
  if (window.dataLayer) {
    window.dataLayer.push({ event: name, ...properties });
  }
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function truncate(value, max) {
  if (typeof value !== 'string') return value;
  return value.length > max ? value.slice(0, max) : value;
}

function getFieldMeta(el) {
  return {
    field_name: el.name || '',
    field_type: el.type || el.tagName.toLowerCase(),
    field_id: el.id || ''
  };
}

// ─── LP-route guard ───────────────────────────────────────────────────────────

function isLPRoute(pathname) {
  return pathname.includes('/lp-');
}

// ─── Listener registry (for clean removal) ────────────────────────────────────

// Each entry: { target, type, handler, options }
const _listeners = [];

function addListener(target, type, handler, options) {
  target.addEventListener(type, handler, options);
  _listeners.push({ target, type, handler, options });
}

// Holds the IntersectionObserver so disableTracking() can disconnect it.
let _intersectionObserver = null;
// Holds the MutationObserver used to pick up late-mounted sections.
let _mutationObserver = null;
// Holds the patched pushState so we can restore it.
let _originalPushState = null;

// ─── Disable tracking ─────────────────────────────────────────────────────────

/**
 * Remove all event listeners, disconnect observers, restore pushState, and set
 * the permanent kill flag. Safe to call multiple times — idempotent after first call.
 * After this returns, trackEvent() and trackStandardEvent() are no-ops for the
 * rest of the session.
 */
export function disableTracking() {
  killed = true;
  isInitialized = false; // allow re-check of guard in popstate handler

  // Remove all registered DOM listeners.
  _listeners.forEach(({ target, type, handler, options }) => {
    try { target.removeEventListener(type, handler, options); } catch (_) {}
  });
  _listeners.length = 0;

  // Disconnect observers.
  if (_intersectionObserver) {
    _intersectionObserver.disconnect();
    _intersectionObserver = null;
  }
  if (_mutationObserver) {
    _mutationObserver.disconnect();
    _mutationObserver = null;
  }

  // Restore original pushState.
  if (_originalPushState) {
    window.history.pushState = _originalPushState;
    _originalPushState = null;
  }

  // Remove the global OTP success hook so it can't fire again.
  delete window.__trackOTPSuccess;
}

// ─── OTP success hook ─────────────────────────────────────────────────────────

/**
 * Called by authService.js after a successful OTP verify response.
 * Fires final conversion events in order, then permanently kills tracking.
 * Exposed on window so authService can call it without a circular import.
 */
function registerOTPSuccessHook() {
  window.__trackOTPSuccess = function () {
    if (killed) return; // already dead
    trackStandardEvent('Lead');
    trackStandardEvent('CompleteRegistration');
    trackStandardEvent('Contact');
    trackEvent('OTP_Verified');
    disableTracking();
  };
}

// ─── Auto-instrumentation ─────────────────────────────────────────────────────

function setupPageView() {
  trackStandardEvent('PageView');

  // Patch pushState — fire PageView on SPA navigation, but also tear down
  // tracking if the user leaves the LP route mid-session.
  _originalPushState = window.history.pushState.bind(window.history);
  window.history.pushState = function (...args) {
    _originalPushState(...args);
    const nextPath = window.location.pathname;
    if (!isLPRoute(nextPath)) {
      disableTracking();
      return;
    }
    trackStandardEvent('PageView');
    seenSections.clear();
  };

  const popstateHandler = () => {
    if (!isLPRoute(window.location.pathname)) {
      disableTracking();
      return;
    }
    trackStandardEvent('PageView');
    seenSections.clear();
  };
  addListener(window, 'popstate', popstateHandler);
}

function setupClickTracking() {
  const handler = (e) => {
    const target = e.target.closest('button, a, [role="button"], [data-track]');
    if (!target) return;

    const text = truncate((target.textContent || '').trim(), 100);
    const props = {
      element_text: text,
      element_id: target.id || '',
      element_class: truncate(target.className || '', 100),
      element_tag: target.tagName.toLowerCase()
    };

    const href = target.getAttribute('href');
    if (href) props.href = href;

    trackEvent('Click', props);
  };
  addListener(document, 'click', handler, { capture: true, passive: true });
}

function setupFieldTracking() {
  const focusinHandler = (e) => {
    const el = e.target;
    if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) return;
    const meta = getFieldMeta(el);
    trackEvent('Field_Focus', meta);
  };
  addListener(document, 'focusin', focusinHandler, { passive: true });

  const focusoutHandler = (e) => {
    const el = e.target;
    if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) return;
    const meta = getFieldMeta(el);
    trackEvent('Field_Blur', meta);
  };
  addListener(document, 'focusout', focusoutHandler, { passive: true });

  const changeHandler = async (e) => {
    const el = e.target;
    if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName)) return;
    const meta = getFieldMeta(el);

    if (isPII(meta.field_name, meta.field_type, meta.field_id)) {
      // Do not include raw value. Hash and emit as advanced matching signal.
      trackEvent('Field_Change', meta);
      const hashed = await hashPII(el.value);
      if (hashed) {
        const matchPayload = {};
        if (PII_PATTERN.test(meta.field_name + meta.field_type + meta.field_id)) {
          // Route to the right key based on what kind of PII field this is.
          if (/email/i.test(meta.field_name + meta.field_type + meta.field_id)) {
            matchPayload.em = hashed;
          } else if (/phone|mobile/i.test(meta.field_name + meta.field_type + meta.field_id)) {
            matchPayload.ph = hashed;
          } else if (/name/i.test(meta.field_name + meta.field_type + meta.field_id)) {
            matchPayload.fn = hashed;
          }
        }
        if (Object.keys(matchPayload).length) {
          window.fbq?.('trackCustom', 'PII_Captured', matchPayload);
        }
      }
    } else {
      const value = truncate(String(el.value ?? ''), 200);
      trackEvent('Field_Change', { ...meta, value });
    }
  };
  addListener(document, 'change', changeHandler, { passive: true });
}

function collectNonPIIFields(form) {
  const fields = {};
  for (const el of form.elements) {
    if (!el.name) continue;
    const meta = getFieldMeta(el);
    if (isPII(meta.field_name, meta.field_type, meta.field_id)) continue;
    fields[el.name] = truncate(String(el.value ?? ''), 200);
  }
  return fields;
}

function setupFormTracking() {
  const handler = (e) => {
    const form = e.target;
    if (form.tagName !== 'FORM') return;

    const formId = form.id || '';
    const formName = form.getAttribute('name') || form.getAttribute('aria-label') || '';
    const fields = collectNonPIIFields(form);

    trackEvent('Form_Submit', { form_id: formId, form_name: formName, fields });
    trackStandardEvent('Lead');

    const isFinalForm =
      form.hasAttribute('data-final-submit') ||
      /final|complete|register/i.test(formId);
    if (isFinalForm) {
      trackStandardEvent('CompleteRegistration');
    }
  };
  addListener(document, 'submit', handler);
}

function setupSectionVisibility() {
  if (!('IntersectionObserver' in window)) return;

  _intersectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const sectionId = el.id || el.getAttribute('data-section') || '';
        if (seenSections.has(sectionId)) return;
        seenSections.add(sectionId);
        const sectionName = el.getAttribute('aria-label') || el.getAttribute('data-section') || sectionId;
        trackEvent('Section_View', { section_id: sectionId, section_name: sectionName });
        _intersectionObserver?.unobserve(el);
      });
    },
    { threshold: 0.5 }
  );

  function observeSections() {
    document.querySelectorAll('[data-section], section[id]').forEach((el) => {
      _intersectionObserver?.observe(el);
    });
  }

  observeSections();

  // React renders async — pick up sections mounted after initTracking runs.
  _mutationObserver = new MutationObserver(observeSections);
  _mutationObserver.observe(document.body, { childList: true, subtree: true });
}

function setupOTPTracking() {
  const handler = (e) => {
    const target = e.target.closest('[data-otp-submit], button');
    if (!target) return;
    const text = (target.textContent || '').trim();
    if (target.hasAttribute('data-otp-submit') || /verify|otp/i.test(text)) {
      trackStandardEvent('Contact');
      trackEvent('OTP_Submit');
    }
  };
  addListener(document, 'click', handler, { capture: true, passive: true });
}

// ─── Init ─────────────────────────────────────────────────────────────────────

/**
 * Wire all auto-instrumentation. Safe to call multiple times — idempotent.
 * Activates only when the current path includes "/lp-". On non-LP routes this
 * is a silent no-op. Call once from App.js top-level useEffect.
 */
export function initTracking() {
  // Permanent kill — OTP success already fired this session, never re-arm.
  if (killed) return;

  // Only activate on landing page routes.
  if (!isLPRoute(window.location.pathname)) return;

  // Already initialized for this LP session.
  if (isInitialized) return;
  isInitialized = true;

  registerOTPSuccessHook();
  setupPageView();
  setupClickTracking();
  setupFieldTracking();
  setupFormTracking();
  setupSectionVisibility();
  setupOTPTracking();
}
